import { X } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { formatNumber, ratioToPercent } from '@/lib/money'
import { cn } from '@/lib/utils'

import type { BulkProgress } from './use-bulk-runner'

export interface BulkActionBarProps {
    count: number
    /** Singular/plural noun, e.g. `['withdrawal', 'withdrawals']`. */
    noun: [singular: string, plural: string]
    onClear: () => void
    /** The buttons. Disable them yourself when `progress.running`. */
    children: ReactNode
    progress?: BulkProgress
    onCancel?: () => void
    className?: string
}

/**
 * Floats over the table once rows are selected, so the actions are reachable
 * without scrolling back to a toolbar and the selection count stays visible while
 * the operator scrolls looking for more rows.
 */
export function BulkActionBar({ count, noun, onClear, children, progress, onCancel, className }: BulkActionBarProps) {
    const running = progress?.running ?? false
    if (count === 0 && !running) return null

    const percent = progress && progress.total > 0 ? ratioToPercent(progress.done / progress.total) : 0

    return (
        <div
            role="region"
            aria-label="Bulk actions"
            className={cn(
                // The bar floats over the bottom edge, where a phone's home indicator
                // sits. `max()` resolves to today's 1rem anywhere there is no inset.
                'pointer-events-none fixed inset-x-0 bottom-[max(1rem,calc(env(safe-area-inset-bottom)+0.5rem))] z-40 flex justify-center px-4',
                'motion-safe:animate-in motion-safe:slide-in-from-bottom-2 motion-safe:fade-in',
                className
            )}>
            <div className="pointer-events-auto flex max-w-full flex-col overflow-hidden rounded-xl border border-border bg-popover shadow-xl">
                <div className="flex flex-wrap items-center gap-3 px-3 py-2">
                    <span className="tabular whitespace-nowrap text-sm font-medium">
                        {formatNumber(count)} {count === 1 ? noun[0] : noun[1]} selected
                    </span>

                    <Separator orientation="vertical" className="h-4" />

                    {/*
                     * Below `sm` the actions become one horizontally scrolling row
                     * rather than a three-row slab that covers the table it acts on.
                     * `min-w-0` is what lets the row shrink far enough to scroll
                     * instead of pushing the bar wider than the screen.
                     */}
                    <div className="flex flex-wrap items-center gap-1.5 max-sm:min-w-0 max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:scroll-hint-x">
                        {children}
                    </div>

                    <Separator orientation="vertical" className="h-4" />

                    {running && onCancel ? (
                        <Button variant="ghost" size="sm" onClick={onCancel} disabled={progress?.cancelling}>
                            {progress?.cancelling ? 'Stopping\u2026' : 'Stop'}
                        </Button>
                    ) : (
                        // Isolated: a separator and 12px of gap on one side, the bar's own
                        // padding on the other, so its slop reaches nothing else.
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={onClear}
                            aria-label="Clear selection"
                            className="touch-target">
                            <X />
                        </Button>
                    )}
                </div>

                {running && progress && (
                    <div className="flex flex-col gap-1 border-t border-border px-3 py-1.5">
                        <div
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={progress.total}
                            aria-valuenow={progress.done}
                            aria-label="Bulk action progress"
                            className="h-1 overflow-hidden rounded-full bg-surface-hover">
                            <div
                                className="h-full bg-primary transition-[width] duration-(--animate-duration)"
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                        <p aria-live="polite" className="tabular text-xs text-muted-foreground">
                            {formatNumber(progress.done)} of {formatNumber(progress.total)}
                            {progress.failed > 0 && (
                                <span className="ml-2 text-destructive-soft-foreground">
                                    {formatNumber(progress.failed)} failed
                                </span>
                            )}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}
